"use client";

import React from "react";
import { ArrowRight, FlaskConical, ShieldCheck, Sparkles, Activity, FileCheck2 } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const keyCapabilities = [{
  title: "Method Validation & PAT",
  desc: "Real-time process monitoring and chemometric modelling."
}, {
  title: "Powder & Tablet Testing",
  desc: "Comprehensive physical characterisation and flowability."
}, {
  title: "Regulated Compliance",
  desc: "Full IQ/OQ/PQ qualification for GMP laboratories."
}];
export default function HeroAnalysis() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="herolaboratoryanalysis" data-nav="dark" className="relative overflow-hidden bg-sidebar text-sidebar-foreground py-16 sm:py-20 lg:py-28">
      {/* Background Decorative Grid and Glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(249,115,22,0.15),rgba(255,255,255,0))]" />
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          
          {/* Content Column */}
          <motion.div className="lg:col-span-7 flex flex-col items-start text-left" variants={containerVariants} initial="hidden" animate="visible">
            {/* Top Pill Badge */}
            <motion.div variants={itemVariants} className="mb-5">
              <Badge variant="outline" className="border-sidebar-border bg-sidebar-accent/50 text-sidebar-foreground px-3.5 py-1 text-xs sm:text-sm font-medium tracking-wide flex items-center gap-2">
                <Sparkles className="size-3.5 text-primary" />
                <span>Precision Analytical Systems</span>
              </Badge>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 variants={itemVariants} className="font-default text-3xl font-bold tracking-tight text-sidebar-foreground sm:text-5xl lg:text-6xl leading-[1.12]">
              Laboratory Analysis <span className="text-primary">Solutions</span>
            </motion.h1>

            {/* Subheading */}
            <motion.p variants={itemVariants} className="mt-6 text-base sm:text-lg text-sidebar-foreground/80 max-w-2xl leading-relaxed">
              Application-led analytical instrumentation, process spectroscopy, and lifecycle support tailored for regulated laboratories and manufacturing facilities across Ireland and the UK.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium shadow-md">
                <Link to="/SolutionsPage" className="inline-flex items-center justify-center gap-2">
                  <span>Explore Solutions</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Your Application
                </Link>
              </Button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div variants={itemVariants} className="mt-10 pt-8 border-t border-sidebar-border/60 grid grid-cols-2 sm:grid-cols-3 gap-4 w-full">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="size-5 text-primary shrink-0" />
                <span className="text-xs sm:text-sm text-sidebar-foreground/85 font-medium">GMP Compliant</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Activity className="size-5 text-primary shrink-0" />
                <span className="text-xs sm:text-sm text-sidebar-foreground/85 font-medium">PAT & Spectroscopy</span>
              </div>
              <div className="flex items-center gap-2.5 col-span-2 sm:col-span-1">
                <FileCheck2 className="size-5 text-primary shrink-0" />
                <span className="text-xs sm:text-sm text-sidebar-foreground/85 font-medium">Full IQ/OQ Support</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Media & Feature Showcase Column */}
          <motion.div className="lg:col-span-5 relative" initial={{
          opacity: prefersReducedMotion ? 1 : 0,
          scale: prefersReducedMotion ? 1 : 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.2
        }}>
            <div className="relative mx-auto max-w-lg lg:max-w-none">
              
              {/* Outer Decorative Glow Card */}
              <div className="relative rounded-2xl border border-sidebar-border bg-sidebar-accent/30 p-2 shadow-2xl backdrop-blur-xs">
                
                {/* Hero Lab Image */}
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-sidebar-accent">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_analysis_spectrometry_equipment.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_analysis_spectrometry_equipment.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_analysis_spectrometry_equipment.webp-wvc-sizes" alt="Laboratory analytical equipment in modern testing environment" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 hover:scale-105" />
                  <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-sidebar/80 via-transparent to-transparent" />

                  {/* Floating Tag inside image */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between rounded-lg bg-sidebar/90 border border-sidebar-border px-3 py-2 text-xs backdrop-blur-sm">
                    <div className="flex items-center gap-2">
                      <FlaskConical className="size-4 text-primary" />
                      <span className="font-semibold text-sidebar-foreground">Online & Benchtop Systems</span>
                    </div>
                    <span className="text-primary font-medium">Certified Support</span>
                  </div>
                </div>
              </div>

              {/* Capability List Cards below Image */}
              <div className="mt-4 space-y-2.5">
                {keyCapabilities.map((cap, i) => <div key={cap.title} data-index={i} className="flex items-start gap-3 rounded-lg border border-sidebar-border bg-sidebar-accent/20 p-3 text-left transition-colors hover:bg-sidebar-accent/40">
                    <div className="mt-0.5 rounded-md bg-primary/10 p-1.5 text-primary">
                      <Activity className="size-4" />
                    </div>
                    <div>
                      <h2 className="text-xs sm:text-sm font-semibold text-sidebar-foreground">
                        {cap.title}
                      </h2>
                      <p className="text-xs text-sidebar-foreground/75 mt-0.5">
                        {cap.desc}
                      </p>
                    </div>
                  </div>)}
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}